import {
  decimalFromStored,
  formatPtBrDecimal,
  type ChangeMark,
  type CompiledFieldNode,
  type CompiledStaticNode,
  type FieldValue,
} from '@record-fill';
import { formatCivilDateToPtBR, parseCivilDate } from '@/shared/lib';
import type { FillContext } from './fill-context';

export type ChangeTone = 'added' | 'fixed';

export interface ChangeHighlight {
  tone: ChangeTone;
  tooltip: string | null;
}

const EMPTY_VALUE = 'vazio';

export const CHANGE_GROUP_CLASSES: Record<ChangeTone, string> = {
  added: 'rounded-md px-2 py-1.5 bg-[var(--color-change-added)] shadow-[inset_0_0_0_1px_var(--color-change-added-border)]',
  fixed: 'rounded-md px-2 py-1.5 bg-[var(--color-change-fixed)] shadow-[inset_0_0_0_1px_var(--color-change-fixed-border)]',
};

export const CHANGE_CELL_FILL: Record<ChangeTone, string> = {
  added: 'var(--color-change-added)',
  fixed: 'var(--color-change-fixed)',
};

function optionLabel(node: CompiledFieldNode | null, value: string): string {
  const control = node?.control;
  const options = control && 'options' in control ? control.options : [];
  return options.find((option) => option.value === value)?.label ?? value;
}

export function formatChangeValue(node: CompiledFieldNode | null, value: FieldValue): string {
  if (value === null) return EMPTY_VALUE;
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((item) => optionLabel(node, item)).join(', ') : EMPTY_VALUE;
  }
  if (node?.control.kind === 'number') {
    const canonical = decimalFromStored(value);
    return canonical === null ? String(value) : formatPtBrDecimal(canonical);
  }
  const text = String(value);
  if (text.trim().length === 0) return EMPTY_VALUE;
  if (node?.control.kind === 'date') {
    const date = parseCivilDate(text);
    return date ? formatCivilDateToPtBR(date) : text;
  }
  return optionLabel(node, text);
}

function toHighlight(mark: ChangeMark, node: CompiledFieldNode | null): ChangeHighlight {
  if (mark.kind === 'filled') return { tone: 'added', tooltip: null };
  return { tone: 'fixed', tooltip: `Antes: ${formatChangeValue(node, mark.before)}` };
}

function visibleMarks(context: FillContext | null) {
  return context?.showHighlights.value === true ? context.changeMarks.value : null;
}

export function fieldChange(context: FillContext | null, node: CompiledFieldNode): ChangeHighlight | null {
  if (!node.state.visible) return null;
  const mark = visibleMarks(context)?.values[node.id];
  return mark ? toHighlight(mark, node) : null;
}

// The device titles the ERP filled are static nodes, so their marks share the field map by node id.
export function titleChange(context: FillContext | null, node: CompiledStaticNode): ChangeHighlight | null {
  if (!node.visible) return null;
  const mark = visibleMarks(context)?.values[node.id];
  return mark ? toHighlight(mark, null) : null;
}

export function extraCellChange(
  context: FillContext | null,
  tableId: string,
  rowIndex: number,
  columnIndex: number,
  node: CompiledFieldNode | null,
): ChangeHighlight | null {
  // The marks are raw, not filtered by what the photo shows, and the cell paints its tint and its
  // focusable "Antes:" tip outside the field's own visibility check: a field a rule hides would
  // still leak the value the rule hides.
  if (node?.state.visible === false) return null;
  const marks = visibleMarks(context);
  if (!marks) return null;
  const mark = marks.cells[tableId]?.[rowIndex]?.[columnIndex];
  if (mark) return toHighlight(mark, node);
  return marks.newRows[tableId]?.includes(rowIndex) ? { tone: 'added', tooltip: null } : null;
}
